import { yupResolver } from '@hookform/resolvers/yup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { DialogTitle } from '@openx/components-core/lib/Dialog/DialogTitle/DialogTitle.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDirectDealsContext } from '../../utils/context/DirectDealsContext.tsx';
import { useRequestDirectDealModalContext } from '../../utils/context/RequestDirectDealModalContext.tsx';
import { defaultValues } from './form/const.ts';
import type { RequestDirectDealForm } from './form/types.ts';
import { transformFormData } from './form/utils.ts';
import { getValidationSchema } from './form/validationSchema.ts';
import { ModalContent } from './ModalContent.tsx';

export const RequestDirectDealModal = () => {
  const { t } = useTranslation();

  const {
    isOpen,
    data: { publisherId, publisherName, callback },
    closeModal,
  } = useRequestDirectDealModalContext();
  const { useDirectDealsContact } = useDirectDealsContext();
  const { requestContact } = useDirectDealsContact();

  const formMethods = useForm<RequestDirectDealForm>({
    defaultValues,
    resolver: yupResolver(getValidationSchema(t)),
    mode: 'all',
  });

  const onFormSubmit = async (formData: RequestDirectDealForm) => {
    await requestContact({ publisherId, publisherName, data: transformFormData(formData), callback });
    onModalClose();
  };

  const onModalClose = () => {
    formMethods.reset();
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <Dialog open={isOpen} onClose={onModalClose} data-test="request-direct-deal-modal">
        <DialogTitle onClose={onModalClose} dataTest="request-direct-deal-modal-title">
          {t('Request a Direct Deal')}
        </DialogTitle>

        <ModalContent publisherName={publisherName} />

        <DialogActions>
          <Button onClick={onModalClose} variant="text">
            {t('Cancel')}
          </Button>

          <Button
            data-test="submit-request"
            color="primary"
            loading={formMethods.formState.isSubmitting}
            onClick={formMethods.handleSubmit(onFormSubmit)}
          >
            {t('Submit request')}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};
